import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import classRepository from '@/api/repositories/classRepository/ClassRepository.ts';

export const useClass = (id: string | undefined) => {
  return useQuery({
    queryKey: ['classes', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await classRepository.findOne({ id });
      if (error) throw new Error(error.message);
      return data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
};

export const useClasses = (schoolId?: string) => {
  return useQuery({
    queryKey: ['classes', schoolId ?? 'all'],
    queryFn: async () => {
      const { data, error } = schoolId
        ? await classRepository.findMany({ school_id: schoolId }, { order: { by: 'created_at', ascending: true } })
        : await classRepository.all({ order: { by: 'created_at', ascending: true } });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateClasses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; school_id: string }[]) => {
      const { data, error } = await classRepository.createMany(payload);
      if (error) throw new Error(error.message);
      if (!data || data.length !== payload.length) {
        throw new Error(`Expected ${payload.length} classes to be created, got ${data?.length ?? 0}`);
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classes'] });
    },
  });
};
